import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class AccessibilityService {
  /** Whether the dyslexia-friendly font is currently active. */
  dyslexiaFont = false;

  /** Whether large text mode is currently active. */
  largeText = false;

  /** Whether reduce motion mode is currently active. */
  reduceMotion = false;

  /** Whether high contrast mode is currently active. */
  highContrast = false;

  /** The currently active color blind filter mode. Defaults to 'none'. */
  colorBlindMode = 'none';

  constructor(private http: HttpClient) {}

  /**
   * Fetches the user's saved accessibility preferences from the API
   * and applies them to the document immediately.
   * @returns {void}
   */
  loadPrefs(): void {
    this.http.get<any>('/api/users/accessibility').subscribe({
      next: (res) => {
        const p = res.accessibility_prefs;
        if (!p) return;
        this.dyslexiaFont = p.dyslexiaFont ?? false;
        this.largeText = p.largeText ?? false;
        this.reduceMotion = p.reduceMotion ?? false;
        this.highContrast = p.highContrast ?? false;
        this.colorBlindMode = p.colorBlindMode ?? 'none';
        this.applyClasses();
      },
      error: (err) => console.error('Failed to load accessibility prefs', err),
    });
  }

  /**
   * Persists the current accessibility preferences to the API.
   * @returns {void}
   */
  savePrefs(): void {
    this.http
      .patch('/api/users/accessibility', {
        dyslexiaFont: this.dyslexiaFont,
        largeText: this.largeText,
        reduceMotion: this.reduceMotion,
        highContrast: this.highContrast,
        colorBlindMode: this.colorBlindMode,
      })
      .subscribe({
        error: (err) => console.error('Failed to save accessibility prefs', err),
      });
  }

  /**
   * Syncs the current preference state to `document.body` CSS classes.
   * Removes all color blind classes before applying the active one,
   * ensuring only one filter is active at a time.
   * @returns {void}
   */
  applyClasses(): void {
    const body = document.body;
    body.classList.toggle('a11y-dyslexia', this.dyslexiaFont);
    body.classList.toggle('a11y-large-text', this.largeText);
    body.classList.toggle('a11y-reduce-motion', this.reduceMotion);
    body.classList.toggle('a11y-high-contrast', this.highContrast);

    ['a11y-deuteranopia', 'a11y-tritanopia', 'a11y-grayscale'].forEach((c) =>
      body.classList.remove(c),
    );

    if (this.colorBlindMode !== 'none') {
      body.classList.add(`a11y-${this.colorBlindMode}`);
    }
  }

  /**
   * Returns true if any accessibility preference is currently active.
   * @returns {boolean}
   */
  get anyActive(): boolean {
    return (
      this.dyslexiaFont ||
      this.largeText ||
      this.reduceMotion ||
      this.highContrast ||
      this.colorBlindMode !== 'none'
    );
  }
}
